import { api } from '@/clients/admin.client';
import { useRouter } from '@tanstack/react-router';
import {
  ActionIcon,
  Button,
  Center,
  Container,
  Menu,
  Stack,
  Table,
  TextInput,
  Text,
} from '@mantine/core';
import { IconDots, IconEdit, IconKey, IconTrash } from '@tabler/icons-react';
import { useForm } from 'react-hook-form';
import { useCallback } from 'react';
import { notifyError, notifySuccess } from '@/lib/notification';
import { modals } from '@mantine/modals';
import { logger } from '@/lib/logger';
import { fmtDate } from '@/lib/date';
import { CodeHighlight } from '@mantine/code-highlight';
import { ApiKey } from '@/clients/types';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

type Props = {
  adminClient: typeof api;
  apiKeys: ApiKey[];
  projectId: string;
  readonly: boolean;
};

export function ProjectApiKeyList({ adminClient, apiKeys, projectId, readonly }: Props) {
  const router = useRouter();

  // Update API KEY Name
  const updateApiKey = useCallback(
    async (apiKey: ApiKey, inputs: Inputs) => {
      try {
        await adminClient.settings_v1_update_project_api_key(
          {
            name: inputs.name,
          },
          {
            params: {
              project_id: projectId,
              api_key_id: apiKey.keys.id!,
            },
          }
        );
        await router.load();
        notifySuccess(`APIキー ${apiKey.spec.name} を更新しました。`);
        modals.closeAll();
      } catch (error) {
        logger.error(error);
        notifyError(`APIキー ${apiKey.spec.name} の更新に失敗しました。`);
      }
    },
    [adminClient, projectId, router]
  );

  const openUpdateApiKeyModal = (apiKey: ApiKey) => {
    modals.open({
      size: 'md',
      title: 'APIキー設定',
      children: (
        <UpdateApiKeyForm
          onSubmit={(inputs) => updateApiKey(apiKey, inputs)}
          defaultValues={{ name: apiKey.spec.name, id: apiKey.keys.id! }}
        />
      ),
    });
  };

  // Delete API KEY
  const deleteApiKey = async (apiKey: ApiKey) => {
    try {
      await adminClient.settings_v1_delete_project_api_key(undefined, {
        params: {
          project_id: projectId,
          api_key_id: apiKey.keys.id!,
        },
      });
      await router.load();
      notifySuccess(`APIキー ${apiKey.spec.name} を削除しました。`);
    } catch (e) {
      logger.error(e);
      notifyError('APIキーの削除に失敗しました。');
    }
  };

  const openConfirmDeleteApiKeyModal = (apiKey: ApiKey) => {
    modals.openConfirmModal({
      title: `APIキー ${apiKey.spec.name} を削除しますか？`,
      children: (
        <Text size="sm">この操作によりAPIキーが削除され、現在のシークレットは無効になります。</Text>
      ),
      onConfirm: () => deleteApiKey(apiKey),
    });
  };

  // Regenerate API KEY
  const handleGenerateApikey = async (apiKey: ApiKey) => {
    try {
      const res = await adminClient.settings_v1_generate_project_api_key(undefined, {
        params: {
          project_id: projectId,
          api_key_id: apiKey.keys.id!,
        },
      });
      await router.load();
      openGenerateApiKeyResult(res.api_key);
    } catch (e) {
      logger.error(e);
      notifyError('シークレットの発行に失敗しました。');
    }
  };

  const openConfirmRegenerateApiKey = (apiKey: ApiKey) => {
    modals.openConfirmModal({
      title: `APIキー ${apiKey.spec.name} のシークレットを再発行しますか？`,
      children: (
        <div className="text-sm">
          この操作により新しいシークレットが作成され、現在のシークレットは無効になります。
        </div>
      ),
      onConfirm: () => handleGenerateApikey(apiKey),
    });
  };

  const openGenerateApiKeyResult = (secret: string) => {
    modals.open({
      title: 'シークレットが発行されました',
      children: (
        <>
          <div className="text-sm">
            シークレットは、この画面を閉じると再表示できません。安全な場所に保存してください。
          </div>
          <div className="my-10">
            <div className="text-sm mb-1">シークレット</div>
            <CodeHighlight
              classNames={{ root: 'border border-plamo-border rounded-lg' }}
              code={secret}
            ></CodeHighlight>
          </div>
        </>
      ),
    });
  };

  return (
    <Table.ScrollContainer minWidth={700} className="plamo-stack-sm">
      <Table horizontalSpacing="lg">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>名前</Table.Th>
            <Table.Th>シークレット</Table.Th>
            <Table.Th>作成日</Table.Th>
            <Table.Th>作成者</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {apiKeys.map((apiKey) => (
            <Table.Tr key={apiKey.keys.id}>
              <Table.Td data-label="名前">{apiKey.spec.name}</Table.Td>
              <Table.Td data-label="シークレット">{apiKey.spec.masked_secret}</Table.Td>
              <Table.Td data-label="作成日">{fmtDate(apiKey.meta?.created_at as string)}</Table.Td>
              <Table.Td data-label="作成者">{apiKey.meta?.created_by}</Table.Td>
              <Table.Td align="right" data-action>
                {!readonly && (
                  <Menu>
                    <Menu.Target>
                      <ActionIcon variant="outline" size="md">
                        <IconDots style={{ width: '70%', height: '70%' }} stroke={1.5} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconEdit style={{ width: '70%', height: '70%' }} />}
                        onClick={() => {
                          openUpdateApiKeyModal(apiKey);
                        }}
                      >
                        設定
                      </Menu.Item>
                      <Menu.Item
                        leftSection={
                          <IconKey
                            style={{ width: '70%', height: '70%' }}
                            className="text-plamo-error"
                          />
                        }
                        onClick={() => {
                          openConfirmRegenerateApiKey(apiKey);
                        }}
                      >
                        <div className="text-plamo-error">再発行</div>
                      </Menu.Item>
                      <Menu.Item
                        leftSection={
                          <IconTrash
                            style={{ width: '70%', height: '70%' }}
                            className="text-plamo-error"
                          />
                        }
                        onClick={() => {
                          openConfirmDeleteApiKeyModal(apiKey);
                        }}
                      >
                        <div className="text-plamo-error">削除</div>
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

const inputSchema = z.object({
  name: z.string().min(1, '必須項目です。').max(63, '63文字以内で入力してください。'),
  id: z.string().min(1),
});
type Inputs = z.infer<typeof inputSchema>;

type FormProps = {
  onSubmit: (data: Inputs) => void;
  defaultValues: Inputs;
};

function UpdateApiKeyForm({ onSubmit, defaultValues }: FormProps) {
  const {
    register,
    handleSubmit,
    formState: { isValid, errors, dirtyFields },
  } = useForm<Inputs>({
    resolver: zodResolver(inputSchema),
    mode: 'onChange',
    defaultValues,
  });
  const isReady = Object.keys(dirtyFields).length > 0 && isValid;

  return (
    <Container m="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Center>
          <Stack w="100%">
            <TextInput
              label="APIキー名"
              placeholder="APIキー名"
              {...register('name', {
                required: 'APIキー名を入力してください。',
              })}
              error={errors.name?.message}
            />
            <Button type="submit" size="sm" disabled={!isReady} mt="lg">
              保存
            </Button>
          </Stack>
        </Center>
      </form>
    </Container>
  );
}
